import { c as _c } from "react/compiler-runtime";
import * as RadixPopover from '@radix-ui/react-popover';
import { cx } from 'class-variance-authority';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'cmdk';
import { AnimatePresence, easeOut, motion } from 'framer-motion';
import { forwardRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../../loading-spinner';
import type { Option, SelectAll } from '../../multi-select';
import CheckboxCheck from './checkbox-check';
type ContentProps = {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  hideSelectAll?: boolean;
  options: Option[];
  onSearchChange?: (search: string) => void;
  emptyState?: ReactNode;
  isLoading?: boolean;
  value: string[];
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  selectAll?: SelectAll;
};
const Content = forwardRef<HTMLDivElement, ContentProps>((t0, ref) => {
  const $ = _c(17);
  const {
    onOpenChange,
    open,
    options,
    value,
    handleInputKeyDown,
    selectAll,
    hideSelectAll,
    onSearchChange,
    emptyState,
    isLoading
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "components.multi-select"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("ui", t1);
  const isAllSelected = value.length === options.length;
  let t2;
  if ($[1] !== emptyState || $[2] !== handleInputKeyDown || $[3] !== hideSelectAll || $[4] !== isAllSelected || $[5] !== isLoading || $[6] !== onOpenChange || $[7] !== onSearchChange || $[8] !== open || $[9] !== options || $[10] !== ref || $[11] !== selectAll || $[12] !== t || $[13] !== value) {
    t2 = open && <RadixPopover.Content className={cx("p-0 w-[var(--radix-popover-trigger-width)] relative z-tooltip", "bg-elevated rounded border border-primary", "shadow-md", "overflow-y-auto origin-[var(--radix-popover-content-transform-origin)]")} align="start" onEscapeKeyDown={() => onOpenChange(false)} side="bottom" sideOffset={4} avoidCollisions={true} collisionPadding={8} collisionBoundary={document.body} asChild={true} forceMount={true}><motion.div ref={ref} initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} transition={{
        duration: 0.1,
        ease: easeOut
      }}><Command shouldFilter={!onSearchChange}><div className="relative"><CommandInput placeholder={t(_temp)} onKeyDown={handleInputKeyDown} onValueChange={onSearchChange} className="w-full pl-3 pr-8 border-b text-tertiary text-body-2 min-h-9 focus:outline-none border-tertiary" />{isLoading && <LoadingSpinner size="compact" className="absolute right-3 top-3 pointer-events-none" />}</div><CommandList><CommandEmpty className="p-3 text-quaternary text-body-2">{emptyState ?? t(_temp2)}</CommandEmpty><CommandGroup className="overflow-y-auto max-h-[300px] py-1">{selectAll && !hideSelectAll && <CommandItem onSelect={selectAll.onSelect} className="px-1 pb-1 mb-1 overflow-hidden border-b border-dashed rounded cursor-pointer border-tertiary"><div className="flex items-center w-full max-w-full gap-2 px-2 py-1 rounded cursor-pointer select-none hover:bg-hover"><span className="relative flex items-center justify-center w-4 h-4 min-w-4 min-h-4"><input type="checkbox" checked={isAllSelected} aria-label={selectAll.label || t(_temp3)} onClick={_temp4} onChange={selectAll.onSelect} className={"\n                          grid w-4 h-full min-w-4 min-h-4\n                          place-content-center\n                          border rounded\n                          bg-primary border-primary\n                          transition-colors duration-100\n                          appearance-none\n                          outline-offset-1\n                          focus:outline-none\n                          checked:bg-tertiary checked:border-transparent\n                          disabled:bg-senary disabled:border-transparent\n                          focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--text-color-accent)]\n                      "} /><CheckboxCheck isSelected={isAllSelected} /></span><span className="w-full font-medium text-body-2 text-primary">{selectAll.label || t(_temp5)}</span></div></CommandItem>}{options.map(option => {
                const isSelected = value.includes(option.value);
                return <CommandItem key={option.value} disabled={option.disabled} onSelect={() => option.toggleOption?.(option.value)} className="px-1 overflow-hidden rounded cursor-pointer data-[disabled=true]:cursor-default"><div className={cx("flex items-start w-full max-w-full gap-2 px-2 py-1 rounded select-none", option.disabled ? "cursor-default" : "cursor-pointer hover:bg-hover")}><span className="relative flex items-center justify-center w-4 h-4 min-w-4 min-h-4 mt-0.5"><input type="checkbox" checked={isSelected} disabled={option.disabled} aria-label={option.label} onClick={_temp6} onChange={() => option.toggleOption?.(option.value)} className={"\n                            grid w-4 h-full min-w-4 min-h-4\n                            place-content-center\n                            border rounded\n                            bg-primary border-primary\n                            transition-colors duration-100\n                            appearance-none\n                            outline-offset-1\n                            focus:outline-none\n                            checked:bg-tertiary checked:border-transparent\n                            disabled:bg-senary disabled:border-transparent\n                            focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--text-color-accent)]\n                        "} /><CheckboxCheck isSelected={isSelected} /></span><span className={cx("flex flex-col w-full text-body-2", option.disabled ? "text-tertiary" : "text-primary")}>{option.label}{option.description && <span className="text-tertiary">{option.description}</span>}</span></div></CommandItem>;
              })}</CommandGroup></CommandList></Command></motion.div></RadixPopover.Content>;
    $[1] = emptyState;
    $[2] = handleInputKeyDown;
    $[3] = hideSelectAll;
    $[4] = isAllSelected;
    $[5] = isLoading;
    $[6] = onOpenChange;
    $[7] = onSearchChange;
    $[8] = open;
    $[9] = options;
    $[10] = ref;
    $[11] = selectAll;
    $[12] = t;
    $[13] = value;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== t2) {
    t3 = <AnimatePresence>{t2}</AnimatePresence>;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
});
export default Content;
function _temp($) {
  return $["search-placeholder-default"];
}
function _temp2($_0) {
  return $_0["empty-state-text-default"];
}
function _temp3($_1) {
  return $_1["select-all-label-default"];
}
function _temp4(event) {
  return event.stopPropagation();
}
function _temp5($_2) {
  return $_2["select-all-label-default"];
}
function _temp6(event_0) {
  return event_0.stopPropagation();
}